import { useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { nanoid } from 'nanoid'

import { firebase } from '../../core/services'

import {
  ANALYTICS_EVENT_CATEGORIES,
  ANALYTICS_EVENTS,
} from '../../common/constants'
import { useCleanupRedux } from '../../common/hooks'
import { apolloService } from '../../common/services'

import { MENU_CURATION_TASKS_QUERY } from '../graphql/queries'
import { FETCH_MENU_CURATION_TASKS } from '../redux/action-types'
import { cleanupFetchMenuCurationTasks } from '../redux/actions'

const useMenuCurationTasksQuery = (
  {
    taskStatus,
    searchText,
    languageIds,
    autoeatStatus,
    tags,
    assignedTierOneCurator,
    assignedCurator,
    assignedQa,
    assignedQc,
    assignedScrapingReviewerUser,
    menuAvailability,
    duplicate,
    backlog,
    curatorId,
    qaId,
    isCurrentUserAssigned,
    isCurrentUserAllocated,
  } = {},
  { sortBy } = {},
  { pageSize, currentPage } = {},
) => {
  const sessionId = useRef(nanoid())
  const dispatch = useDispatch()
  useCleanupRedux(cleanupFetchMenuCurationTasks)

  const { isLoading, error, data } = useSelector(
    store => store.menuCurationTasks.menuCurationTasks,
  )

  const refetch = useCallback(
    (filtersParam, sortersParam, paginationParam) => {
      firebase.logEvent(
        ANALYTICS_EVENTS.MENU_CURATOR_LOAD_MENU_TASKS
          .MENU_CURATOR_START_LOAD_MENU_TASKS,
        {
          sessionId: sessionId.current,
          ...filtersParam,
          ...sortersParam,
          ...paginationParam,
          eventCategory:
            ANALYTICS_EVENT_CATEGORIES.MENU_CURATOR_LOAD_MENU_TASKS,
        },
      )

      apolloService.query({
        query: MENU_CURATION_TASKS_QUERY,
        variables: {
          filters: filtersParam,
          sorters: sortersParam,
          pagination: paginationParam,
        },
        dispatch,
        onSuccess: () =>
          firebase.logEvent(
            ANALYTICS_EVENTS.MENU_CURATOR_LOAD_MENU_TASKS
              .MENU_CURATOR_END_LOAD_MENU_TASKS,
            {
              sessionId: sessionId.current,
              ...filtersParam,
              ...sortersParam,
              ...paginationParam,
              eventCategory:
                ANALYTICS_EVENT_CATEGORIES.MENU_CURATOR_LOAD_MENU_TASKS,
            },
          ),
        action: FETCH_MENU_CURATION_TASKS,
      })
    },
    [dispatch],
  )

  useEffect(() => {
    refetch(
      {
        taskStatus,
        searchText,
        languageIds,
        autoeatStatus,
        tags,
        assignedTierOneCurator,
        assignedCurator,
        assignedQa,
        assignedQc,
        assignedScrapingReviewerUser,
        menuAvailability,
        duplicate,
        backlog,
        curatorId,
        qaId,
        isCurrentUserAssigned,
        isCurrentUserAllocated,
      },
      {
        sortBy,
      },
      { pageSize, currentPage },
    )
  }, [
    duplicate,
    backlog,
    refetch,
    taskStatus,
    searchText,
    languageIds,
    autoeatStatus,
    tags,
    assignedTierOneCurator,
    assignedCurator,
    assignedQa,
    assignedScrapingReviewerUser,
    isCurrentUserAllocated,
    sortBy,
    pageSize,
    currentPage,
    assignedQc,
    curatorId,
    qaId,
    menuAvailability,
    isCurrentUserAssigned,
  ])

  return { isLoading, error, data }
}

export default useMenuCurationTasksQuery
